import axios from 'axios';

import { starbookApi, requestConfig } from './api-config';

export default class StarbookApi {
  static async checkAuth() {
    const info = await axios.get(`${starbookApi}/app/info`, requestConfig);
    requestConfig.headers = {
      ...requestConfig.headers,
      'X-CSRF-TOKEN': info.data.csrfToken,
    }
    return info;
  }

  static logout() {
    return axios.get(`${starbookApi}/account/logout`);
  }

  static fetchStarbookData(params) {
    return axios.post(`${starbookApi}/users/query`, params, requestConfig);
  }

  static fetchQueryData(params) {
    return axios.get(`${starbookApi}/users/query-meta`, requestConfig);
  }

  // NEW: Fetch basic users for dropdowns
  static fetchBasicUsers() {
    return axios.get(`${starbookApi}/users/query-basic`, requestConfig);
  }

  static getSkills() {
    return axios.get(`${starbookApi}/skills/query`, requestConfig);
  }

  static createSkillCategory(params) {
    return axios.post(`${starbookApi}/skills/create/category`, params, requestConfig);
  }

  static createSkillSubCategory(params) {
    return axios.post(`${starbookApi}/skills/create/subcategory`, params, requestConfig);
  }

  static createSkillset(params) {
    return axios.post(`${starbookApi}/skills/create/skillset`, params, requestConfig);
  }

  static updateSkillCategory(params) {
    return axios.post(`${starbookApi}/skills/update/category`, params, requestConfig);
  }

  static updateSkillSubCategory(params) {
    return axios.post(`${starbookApi}/skills/update/subcategory`, params, requestConfig);
  }

  static updateSkillset(params) {
    return axios.post(`${starbookApi}/skills/update/skillset`, params, requestConfig);
  }

  static deleteSkillCategory(params) {
    return axios.post(`${starbookApi}/skills/delete/category`, params, requestConfig);
  }

  static deleteSkillSubCategory(params) {
    return axios.post(`${starbookApi}/skills/delete/subcategory`, params, requestConfig);
  }

  static deleteSkillset(params) {
    return axios.post(`${starbookApi}/skills/delete/skillset`, params, requestConfig);
  }

  static countForDelete(params) {
    return axios.get(`${starbookApi}/skills/count/${params.idType}/${params.id}`, requestConfig);
  }

  static delete(params) {
    return axios.post(
      `${starbookApi}/skills/delete/${params.type}`,
      { id: params.id },
      requestConfig,
    );
  }

  static fetchDepartments() {
    return axios.get(`${starbookApi}/departments`, requestConfig);
  }

  static setDepartments(params) {
    return axios.post(`${starbookApi}/departments/set`, params, requestConfig);
  }

  static getUserDetails(params) {
    return axios.get(`${starbookApi}/users/${params.type}/${params.id}`, requestConfig);
  }

  static postUserMeta(params) {
    return axios.post(
      `${starbookApi}/users/update/${params.userId}/meta`,
      params.data,
      requestConfig,
    );
  }

  static uploadProfilePicture(params) {
    return axios.post(
      `${starbookApi}/users/update/${params.id}/profile-pic?type=${params.type}`, params.data, {
      ...requestConfig,
        onUploadProgress: (ProgressEvent) => params.progressFunc(ProgressEvent),
      },
    );
  }

  static updateCvMeta(params) {
    return axios.post(
      `${starbookApi}/users/update/${params.id}/cv-defaults/meta`,
      params.data,
      requestConfig,
    );
  }

  static updateCvLanguage(params) {
    return axios.post(
      `${starbookApi}/users/update/${params.id}/cv-defaults/languages`,
      params.data,
      requestConfig,
    );
  }

  static updateCvEducations(params) {
    return axios.post(
      `${starbookApi}/users/update/${params.id}/cv-defaults/education`,
      params.data,
      requestConfig,
    );
  }

  static updateCvCertificates(params) {
    return axios.post(
      `${starbookApi}/users/update/${params.id}/cv-defaults/certificates`,
      params.data,
      requestConfig,
    );
  }

  static updateCvExperiences(params) {
    return axios.post(
      `${starbookApi}/users/update/${params.id}/cv-defaults/experiences`,
      params.data,
      requestConfig,
    );
  }

  static updateSkills(params) {
    return axios.post(
      `${starbookApi}/users/update/${params.id}/skills`,
      params.data,
      requestConfig,
    );
  }

  static setPermissions(params) {
    return axios.post(
      `${starbookApi}/users/update/${params.id}/permissions`,
      params.data,
      requestConfig,
    );
  }

  static setLocation(params) {
    return axios.post(
      `${starbookApi}/users/update/${params.id}/location?locationId=${params.data.id}`,
      null,
      requestConfig,
    );
  }

  // NEW: Delete user
  static deleteUser(userId) {
    return axios.post(
      `${starbookApi}/users/${userId}/delete`,
      null,
      requestConfig,
    );
  }

  static updateUserData(params) {
    const formData = new FormData();
    
    // Required fields
    formData.append('fullName', params.data.fullName);
    formData.append('email', params.data.email);
    formData.append('position', params.data.position);
    formData.append('departmentId', params.data.departmentId);

    const permissions = params.data.permissions || ['public'];
    permissions.forEach((permission, index) => {
      formData.append('permissions', permission);
    });
    
    // Employed date
    if (params.data.employedDate instanceof Date) {
      formData.append('employedDate', params.data.employedDate.toISOString());
    } else if (params.data.employedDate) {
      formData.append('employedDate', params.data.employedDate);
    }
    
    // Optional fields - only append if they have values
    if (params.data.managerId) formData.append('managerId', params.data.managerId);
    if (params.data.dumId) formData.append('dumId', params.data.dumId);
    if (params.data.privatePhoneNo) formData.append('privatePhoneNo', params.data.privatePhoneNo);
    if (params.data.companyPhoneNo) formData.append('companyPhoneNo', params.data.companyPhoneNo);
    if (params.data.nickName) formData.append('nickName', params.data.nickName);
    if (params.data.bio) formData.append('bio', params.data.bio);
    if (params.data.horoscope) formData.append('horoscope', params.data.horoscope);

    console.log('Sending data:', Object.fromEntries(formData));

    return axios.post(
      `${starbookApi}/users/update/${params.userId}`,
      formData,
      {
        ...requestConfig,
        headers: {
          ...requestConfig.headers,
          'Content-Type': 'multipart/form-data',
        },
      },
    );
  }

static createUserData(params) {
  const formData = new FormData();
  
  // Required fields
  formData.append('fullName', params.fullName);
  formData.append('email', params.email);
  formData.append('position', params.position);
  formData.append('departmentId', params.departmentId);
  
  const permissions = params.permissions || ['public'];
  permissions.forEach((permission) => {
    formData.append('permissions', permission);
  });
  
  // Employed date
  if (params.employedDate instanceof Date) {
    formData.append('employedDate', params.employedDate.toISOString());
  } else if (params.employedDate) {
    formData.append('employedDate', params.employedDate);
  }
  
  // Optional fields - only append if they have values
  if (params.managerId) formData.append('managerId', params.managerId);
  if (params.dumId) formData.append('dumId', params.dumId);
  if (params.privatePhoneNo) formData.append('privatePhoneNo', params.privatePhoneNo);
  if (params.companyPhoneNo) formData.append('companyPhoneNo', params.companyPhoneNo);
  if (params.nickName) formData.append('nickName', params.nickName);
  if (params.bio) formData.append('bio', params.bio);
  if (params.horoscope) formData.append('horoscope', params.horoscope);
  
  // Profile image
  if (params.profileImage && params.imageType !== '') {
    formData.append('profileImage', params.profileImage);
    formData.append('imageType', params.imageType);
  }

  console.log('Creating user with data:', Object.fromEntries(formData));

  return axios.post(
    `${starbookApi}/users/create`,
    formData,
    {
      ...requestConfig,
      headers: {
        ...requestConfig.headers,
        'Content-Type': 'multipart/form-data',
      },
    },
  );
  }

  static fetchPESOHistoryData(params) {
    return axios.post(`${starbookApi}/pesos/admin/history`, params, requestConfig);
  }

  static fetchPESOUserHistoryData(params) {
    return axios.post(`${starbookApi}/pesos/history`, params, requestConfig);
  }

  static postPesoTransfer(params) {
    return axios.post(`${starbookApi}/pesos/transfer`, params, requestConfig);
  }

  static postPesoGive(params) {
    return axios.post(`${starbookApi}/pesos/admin/give`, params, requestConfig);
  }

  static postPesoTake(params) {
    return axios.post(`${starbookApi}/pesos/admin/take`, params, requestConfig);
  }
}